import type { AttributeValueSettings } from "@/lib/categories/attribute-definition";

export type AttributeLabels = Record<string, Record<string, string>>;

export type DraftField =
  | "attributes"
  | "title"
  | "description"
  | "descriptionTarget"
  | "labels";

let nextUid = 0;

/**
 * A stable React key. Rows are reordered and removed, and an array index as a
 * key makes React reuse the wrong DOM node when they are.
 */
export function newRowUid(): string {
  nextUid += 1;
  return `row-${nextUid}`;
}

export interface AttributeRow extends AttributeValueSettings {
  /** Identity for rendering only; never sent to the API. */
  uid: string;
  /** Frozen once saved; derived from `label` while `isNew`. */
  key: string;
  label: string;
  description: string;
  isNew: boolean;
}

export interface CategoryDraft {
  attributes: AttributeRow[];
  titleTemplate: string;
  descriptionTemplate: string;
  descriptionMode: "replace" | "extend";
  descriptionTargetWordCount: number | null;
  labels: AttributeLabels;
  touched: Set<DraftField>;
}

export interface DraftStore {
  get(id: string): CategoryDraft | undefined;
  set(id: string, draft: CategoryDraft): void;
  clear(id: string): void;
  has(id: string): boolean;
  ids(): string[];
}

export function emptyDraft(): CategoryDraft {
  return {
    attributes: [],
    titleTemplate: "",
    descriptionTemplate: "",
    descriptionMode: "replace",
    descriptionTargetWordCount: null,
    labels: {},
    touched: new Set(),
  };
}

/** Records that a field changed. Returns a new draft; never mutates. */
export function touchDraft(draft: CategoryDraft, field: DraftField): CategoryDraft {
  const touched = new Set(draft.touched);
  touched.add(field);
  return { ...draft, touched };
}

/**
 * Drafts live per category so selecting another one never discards work — the
 * previous editor refetched on id change and silently threw edits away.
 */
export function createDraftStore(): DraftStore {
  const drafts = new Map<string, CategoryDraft>();
  return {
    get: (id) => drafts.get(id),
    set: (id, draft) => void drafts.set(id, draft),
    clear: (id) => void drafts.delete(id),
    has: (id) => drafts.has(id),
    ids: () => [...drafts.keys()],
  };
}
